import { BACKEND_URL } from './env';
import { API_PATHS, API_ROUTES } from './apiRoutes';
import { API_CONTRACT_POLICY, type ApiErrorEnvelope } from './contracts';
import { isNativeCapacitorClient, readNativeAccessToken } from './nativeAuth';

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

const DEFAULT_TIMEOUT_MS = 12_000;
const DEFAULT_RETRIES = 2;
const DEFAULT_RETRY_DELAY_MS = 350;
// 429 is intentionally excluded — retrying rate limits amplifies load and extends lockout.
const RETRYABLE_STATUSES = new Set([408, 425, 500, 502, 503, 504]);
const RETRYABLE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS', 'PUT', 'DELETE']);
const MUTATING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);
const MAX_RETRIES = 5;
const MIN_TIMEOUT_MS = 250;
const MIN_RETRY_DELAY_MS = 50;


export class ApiError extends Error {
  status: number;
  data: ApiErrorEnvelope | null;
  traceId: string | undefined;
  code: string | undefined;

  constructor(message: string, status: number, data: ApiErrorEnvelope | null, traceId?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
    this.traceId = traceId;
    this.code = data?.code;
  }
}

const buildTraceId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `trace-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const readCookie = (name: string): string | undefined => {
  if (typeof document === 'undefined') return undefined;
  const matches = document.cookie.match(new RegExp(`(?:^|; )${name.replace(/[-[\]/{}()*+?.\\^$|]/g, '\\$&')}=([^;]*)`));
  return matches?.[1] ? decodeURIComponent(matches[1]) : undefined;
};

const isCsrfFailure = (error: ApiError): boolean =>
  error.status === 403 &&
  (/csrf/i.test(error.message) ||
    /csrf/i.test(String(error.code || '')) ||
    /csrf/i.test(String(error.data?.message || '')));

/**
 * Cookie-auth POSTs need a script-readable csrf_token for the x-csrf-token header.
 * After session-marker clears (or host-only cookie drift), restamp via a safe GET
 * so ensureCsrfCookie can mint/re-domain the shared cookie before retrying.
 */
let csrfProbeInFlight: Promise<string | undefined> | null = null;
const ensureReadableCsrfToken = async (): Promise<string | undefined> => {
  if (typeof document === 'undefined' || isNativeCapacitorClient()) {
    return readCookie('csrf_token');
  }
  const existing = readCookie('csrf_token');
  if (existing) return existing;
  if (!csrfProbeInFlight) {
    csrfProbeInFlight = (async () => {
      try {
        await fetch(`${BACKEND_URL}${API_ROUTES.v1.health}`, {
          method: 'GET',
          credentials: 'include',
          headers: { Accept: 'application/json' }
        });
      } catch {
        // Best-effort restamp; caller still attempts the original request.
      } finally {
        csrfProbeInFlight = null;
      }
      return readCookie('csrf_token');
    })();
  }
  return csrfProbeInFlight;
};

let memoryAccessTokenFallback: string | null = null;

export const getStoredAccessToken = (): string | null => {
  if (typeof window === 'undefined') return memoryAccessTokenFallback;
  try {
    return sessionStorage.getItem('ninpo_access_token') || localStorage.getItem('ninpo_access_token') || memoryAccessTokenFallback;
  } catch {
    return memoryAccessTokenFallback;
  }
};

export const setStoredAccessToken = (token: string | null | undefined) => {
  memoryAccessTokenFallback = token || null;
  if (typeof window === 'undefined') return;
  try {
    if (token) {
      sessionStorage.setItem('ninpo_access_token', token);
      localStorage.setItem('ninpo_access_token', token);
    } else {
      sessionStorage.removeItem('ninpo_access_token');
      localStorage.removeItem('ninpo_access_token');
    }
  } catch {
    /* Safe fallback to transient memory token */
  }
};

/** Clear script-readable session markers so a failed refresh does not loop on boot.
 *  Do not clear csrf_token here: wiping it while httpOnly access_token remains causes
 *  cookie-auth POSTs (e.g. support chat) to 403 with Missing or invalid CSRF token.
 */
export const clearBrowserSessionMarkers = () => {
  setStoredAccessToken(null);
  if (typeof document === 'undefined') return;
  const expire = 'Thu, 01 Jan 1970 00:00:00 GMT';
  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  const parentDomain =
    host.endsWith('ninposnacks.com') && host !== 'ninposnacks.com'
      ? '; Domain=.ninposnacks.com'
      : host === 'ninposnacks.com'
        ? '; Domain=.ninposnacks.com'
        : '';
  for (const name of ['has_session'] as const) {
    document.cookie = `${name}=; Path=/; Expires=${expire}; SameSite=Lax${parentDomain}`;
    document.cookie = `${name}=; Path=/; Expires=${expire}; SameSite=Lax`;
    document.cookie = `${name}=; Path=/; Expires=${expire}; SameSite=Lax; Secure${parentDomain}`;
    document.cookie = `${name}=; Path=/; Expires=${expire}; SameSite=Lax; Secure`;
    document.cookie = `${name}=; Path=/; Expires=${expire}${parentDomain}`;
    document.cookie = `${name}=; Path=/; Expires=${expire}`;
    document.cookie = `${name}=; Path=/; Expires=${expire}; Secure${parentDomain}`;
    document.cookie = `${name}=; Path=/; Expires=${expire}; Secure`;
  }
};

const normalizeErrorEnvelope = (raw: unknown): ApiErrorEnvelope | null => {
  if (!raw || typeof raw !== 'object') return null;
  const entry = raw as Record<string, unknown>;
  const nestedError =
    entry.error && typeof entry.error === 'object' && !Array.isArray(entry.error)
      ? (entry.error as Record<string, unknown>)
      : null;

  const messageCandidate =
    (typeof entry.message === 'string' ? entry.message : null) ||
    (typeof entry.error === 'string' ? entry.error : null) ||
    (typeof nestedError?.message === 'string' ? nestedError.message : null);

  const codeCandidate =
    (typeof entry.code === 'string' ? entry.code : null) ||
    (typeof nestedError?.code === 'string' ? nestedError.code : null);

  if ((!messageCandidate || !messageCandidate.trim()) && !codeCandidate) {
    return null;
  }

  const envelope: ApiErrorEnvelope = {
    message: (messageCandidate && messageCandidate.trim()) || codeCandidate || 'Request failed'
  };
  if (typeof entry.error === 'string') envelope.error = entry.error;
  if (codeCandidate) envelope.code = codeCandidate;
  if (typeof entry.traceId === 'string') envelope.traceId = entry.traceId;
  if (typeof entry.status === 'number') envelope.status = entry.status;
  if (entry.details !== undefined) envelope.details = entry.details;
  else if (entry.data !== undefined) envelope.details = entry.data;
  return envelope;
};

const parseErrorMessage = (data: ApiErrorEnvelope | null, fallback: string): string =>
  data?.message || data?.error || fallback;

const withTraceHeaders = (headers: Headers): Headers => {
  if (!headers.has(API_CONTRACT_POLICY.requiredHeaders.traceId)) {
    headers.set(API_CONTRACT_POLICY.requiredHeaders.traceId, buildTraceId());
  }
  if (!headers.has(API_CONTRACT_POLICY.requiredHeaders.clientRequestId)) {
    headers.set(API_CONTRACT_POLICY.requiredHeaders.clientRequestId, buildTraceId());
  }
  return headers;
};

const shouldRetry = (status: number, method: string): boolean =>
  RETRYABLE_METHODS.has(method) && RETRYABLE_STATUSES.has(status);

const normalizeRetryCount = (value: number | undefined): number => {
  if (!Number.isFinite(value)) return DEFAULT_RETRIES;
  return Math.min(MAX_RETRIES, Math.max(0, Math.trunc(value ?? DEFAULT_RETRIES)));
};

const normalizeDuration = (value: number | undefined, fallback: number, minValue: number): number => {
  if (!Number.isFinite(value)) return fallback;
  return Math.max(minValue, Math.trunc(value ?? fallback));
};

const computeRetryDelay = (retryDelayMs: number, attempt: number): number =>
  retryDelayMs * Math.pow(2, attempt);

const toApiError = (
  error: unknown,
  fallbackMessage: string,
  details?: Record<string, unknown>
): ApiError => {
  if (error instanceof ApiError) return error;
  return new ApiError(fallbackMessage, 0, {
    message: fallbackMessage,
    details: {
      ...(details || {}),
      cause: error instanceof Error ? error.message : String(error)
    }
  });
};

const shouldAutoApplyJsonContentType = (body: BodyInit | null | undefined): boolean => {
  if (!body) return false;
  const tag = Object.prototype.toString.call(body);
  if (tag === '[object FormData]') return false;
  if (tag === '[object URLSearchParams]') return false;
  if (tag === '[object Blob]' || tag === '[object File]') return false;
  if (tag === '[object ArrayBuffer]') return false;
  if (tag === '[object ReadableStream]') return false;
  if (ArrayBuffer.isView(body)) return false;
  return true;
};

const ensureAllowedApiOrigin = (url: URL): URL => {
  const backendOrigin = new URL(BACKEND_URL).origin;
  if (url.origin !== backendOrigin) {
    throw new ApiError('Blocked cross-origin API request', 0, {
      message: 'Blocked cross-origin API request',
      details: {
        origin: url.origin,
        allowedOrigin: backendOrigin
      }
    });
  }
  return url;
};

const buildRequestUrl = (
  path: string,
  query: Record<string, string | number | boolean | null | undefined> | undefined
): string => {
  const backend = new URL(BACKEND_URL);
  const url = path.startsWith('http')
    ? ensureAllowedApiOrigin(new URL(path))
    : new URL(path.startsWith('/') ? path : `/${path}`, backend);

  if (query && typeof query === 'object') {
    for (const [key, rawValue] of Object.entries(query)) {
      if (rawValue === null || rawValue === undefined) continue;
      const value = String(rawValue).trim();
      if (!value) continue;
      url.searchParams.set(key, value);
    }
  }
  return url.toString();
};

const ensureJsonResponse = async (response: Response, method: string): Promise<unknown | null> => {
  if (response.status === 204 || method === 'HEAD') return null;

  const contentType = response.headers.get('content-type') || '';
  const responseText = await response.text();
  const hasBody = responseText.trim().length > 0;

  if (!hasBody) return null;

  const normalizedContentType = contentType.toLowerCase();
  if (!normalizedContentType.includes('application/json') && !normalizedContentType.includes('+json')) {
    throw new ApiError('Expected JSON response from server', response.status, {
      message: 'Expected JSON response from server',
      details: {
        contentType
      }
    });
  }

  try {
    return JSON.parse(responseText);
  } catch {
    throw new ApiError('Invalid JSON response from server', response.status, {
      message: 'Invalid JSON response from server'
    });
  }
};

const mergeAbortSignals = (
  externalSignal: AbortSignal | undefined,
  timeoutMs: number
): { signal: AbortSignal; cleanup: () => void } => {
  const normalizedTimeoutMs = normalizeDuration(timeoutMs, DEFAULT_TIMEOUT_MS, MIN_TIMEOUT_MS);
  const timeoutController = new AbortController();
  const timeoutId = setTimeout(() => timeoutController.abort(), normalizedTimeoutMs);

  if (!externalSignal) {
    const cleanup = () => clearTimeout(timeoutId);
    timeoutController.signal.addEventListener('abort', cleanup, { once: true });
    return { signal: timeoutController.signal, cleanup };
  }

  if (externalSignal.aborted) {
    clearTimeout(timeoutId);
    return { signal: externalSignal, cleanup: () => {} };
  }

  const composite = new AbortController();
  const abort = () => composite.abort();
  externalSignal.addEventListener('abort', abort, { once: true });
  timeoutController.signal.addEventListener('abort', abort, { once: true });

  const cleanup = () => {
    clearTimeout(timeoutId);
    externalSignal.removeEventListener('abort', abort);
    timeoutController.signal.removeEventListener('abort', abort);
  };

  composite.signal.addEventListener('abort', cleanup, { once: true });

  return { signal: composite.signal, cleanup };
};

let authFailureHandler: ((error: ApiError) => void) | null = null;
type BrowserRefreshResult = 'ok' | 'auth_dead' | 'transient';
let cookieRefreshInFlight: Promise<BrowserRefreshResult> | null = null;

const tryRefreshBrowserSession = async (): Promise<BrowserRefreshResult> => {
  if (isNativeCapacitorClient()) return 'transient';
  if (typeof document === 'undefined' || !readCookie('has_session')) return 'auth_dead';
  if (!cookieRefreshInFlight) {
    cookieRefreshInFlight = (async () => {
      const attemptRefresh = async (): Promise<BrowserRefreshResult> => {
        const csrf = (await ensureReadableCsrfToken()) || readCookie('csrf_token');
        const headers = new Headers({
          Accept: 'application/json',
          'Content-Type': 'application/json'
        });
        if (csrf) headers.set('x-csrf-token', csrf);
        headers.set(API_CONTRACT_POLICY.requiredHeaders.traceId, buildTraceId());
        const abortController = new AbortController();
        const timeoutId = setTimeout(() => abortController.abort(), 10000);
        let response: Response;
        try {
          response = await fetch(`${BACKEND_URL}${API_PATHS.auth.refresh}`, {
            method: 'POST',
            credentials: 'include',
            headers,
            body: JSON.stringify({}),
            signal: abortController.signal
          });
        } finally {
          clearTimeout(timeoutId);
        }
        if (!response.ok) {
          // Only wipe markers on definitive auth rejection. CSRF / 5xx are transient.
          if (response.status === 401) {
            clearBrowserSessionMarkers();
            return 'auth_dead';
          }
          return 'transient';
        }
        // Keep stored Bearer in sync with rotated access JWT; otherwise the next
        // apiRequest still sends the expired token and forces a false logout.
        try {
          const payload = (await response.json()) as { data?: { accessToken?: string }; accessToken?: string };
          const nextAccess =
            String(payload?.data?.accessToken || payload?.accessToken || '').trim() || null;
          if (nextAccess) setStoredAccessToken(nextAccess);
        } catch {
          // Cookie session may still be valid even if body parse fails.
          // Prefer cookie-only auth on retry rather than replaying a stale Bearer.
          setStoredAccessToken(null);
        }
        return 'ok';
      };

      try {
        const first = await attemptRefresh();
        if (first === 'transient') {
          // One CSRF restamp retry covers stale double-submit tokens after idle tabs.
          await ensureReadableCsrfToken();
          return await attemptRefresh();
        }
        return first;
      } catch {
        // Network/timeouts must not clear session markers — that causes false logouts.
        return 'transient';
      } finally {
        cookieRefreshInFlight = null;
      }
    })();
  }
  return cookieRefreshInFlight;
};

export const registerAuthFailureHandler = (handler: ((error: ApiError) => void) | null) => {
  authFailureHandler = handler;
};

export type ApiRequestOptions = RequestInit & {
  retries?: number;
  retryDelayMs?: number;
  timeoutMs?: number;
  skipJsonContentType?: boolean;
  idempotencyKey?: string;
  requireIdempotencyKey?: boolean;
  query?: Record<string, string | number | boolean | null | undefined>;
};

export async function apiRequest<T>(path: string, options: ApiRequestOptions = {}): Promise<T> {
  const {
    retries: inputRetries = DEFAULT_RETRIES,
    retryDelayMs: inputRetryDelayMs = DEFAULT_RETRY_DELAY_MS,
    timeoutMs: inputTimeoutMs = DEFAULT_TIMEOUT_MS,
    skipJsonContentType = false,
    idempotencyKey,
    requireIdempotencyKey = false,
    query,
    headers: inputHeaders,
    signal,
    method = 'GET',
    ...restOptions
  } = options;

  const normalizedPath = String(path || '').trim();
  if (!normalizedPath) {
    throw new ApiError('API path is required', 0, { message: 'API path is required' });
  }

  const retries = normalizeRetryCount(inputRetries);
  const retryDelayMs = normalizeDuration(inputRetryDelayMs, DEFAULT_RETRY_DELAY_MS, MIN_RETRY_DELAY_MS);
  const timeoutMs = normalizeDuration(inputTimeoutMs, DEFAULT_TIMEOUT_MS, MIN_TIMEOUT_MS);
  const url = buildRequestUrl(normalizedPath, query);
  const normalizedMethod = method.toUpperCase();
  const headers = withTraceHeaders(new Headers(inputHeaders || {}));
  headers.set('Accept', 'application/json');
  if (isNativeCapacitorClient()) {
    headers.set('x-ninpo-client', 'capacitor');
  }
  const activeBearerToken = isNativeCapacitorClient() ? readNativeAccessToken() : getStoredAccessToken();
  if (activeBearerToken && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${activeBearerToken}`);
  }
  // Cookie-session mutations (no Bearer) require CSRF double-submit.
  let csrfRecoveryAttempted = false;
  if (MUTATING_METHODS.has(normalizedMethod) && !headers.has('Authorization')) {
    const csrfToken = (await ensureReadableCsrfToken()) || readCookie('csrf_token');
    if (csrfToken && !headers.has('x-csrf-token')) {
      headers.set('x-csrf-token', csrfToken);
    }
  } else if (MUTATING_METHODS.has(normalizedMethod)) {
    const csrfToken = readCookie('csrf_token');
    if (csrfToken && !headers.has('x-csrf-token')) {
      headers.set('x-csrf-token', csrfToken);
    }
  }

  if (
    !skipJsonContentType
    && shouldAutoApplyJsonContentType(restOptions.body as BodyInit | null | undefined)
    && !headers.has('Content-Type')
  ) {
    headers.set('Content-Type', 'application/json');
  }

  const normalizedIdempotencyKey = idempotencyKey?.trim() || '';
  if (normalizedIdempotencyKey) {
    headers.set(API_CONTRACT_POLICY.requiredHeaders.idempotencyKey, normalizedIdempotencyKey);
  } else if (requireIdempotencyKey) {
    throw new ApiError('Explicit Idempotency-Key is required for this API request', 0, {
      message: 'Explicit Idempotency-Key is required for this API request',
      code: 'IDEMPOTENCY_KEY_REQUIRED',
      details: {
        path: normalizedPath,
        method: normalizedMethod
      }
    });
  }

  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const { signal: requestSignal, cleanup: cleanupAbort } = mergeAbortSignals(signal ?? undefined, timeoutMs);
      let response: Response;
      try {
        response = await fetch(url, {
          ...restOptions,
          method: normalizedMethod,
          headers,
          signal: requestSignal,
          credentials: isNativeCapacitorClient() ? 'omit' : 'include'
        });
      } finally {
        cleanupAbort();
      }

      const responseTraceId =
        response.headers.get('x-trace-id') || response.headers.get('x-request-id') || undefined;
      const payload = await ensureJsonResponse(response, normalizedMethod);
      const errorData = normalizeErrorEnvelope(payload);

      if (response.ok) {
        return payload as T;
      }

      const error = new ApiError(
        parseErrorMessage(errorData, `Request failed (${response.status})`),
        response.status,
        errorData,
        responseTraceId
      );

      // Cookie-auth CSRF miss: restamp csrf cookie once, then retry (do not treat as logout).
      // Support chat and other callers often set retries:0 — decrement attempt so recovery
      // does not fall through to "Retry policy exhausted".
      if (
        !csrfRecoveryAttempted &&
        !headers.has('Authorization') &&
        isCsrfFailure(error) &&
        MUTATING_METHODS.has(normalizedMethod) &&
        !isNativeCapacitorClient()
      ) {
        csrfRecoveryAttempted = true;
        headers.delete('x-csrf-token');
        try {
          await fetch(`${BACKEND_URL}${API_ROUTES.v1.health}`, {
            method: 'GET',
            credentials: 'include',
            headers: { Accept: 'application/json' }
          });
        } catch {
          // Best-effort restamp.
        }
        const restamped = readCookie('csrf_token');
        if (restamped) {
          headers.set('x-csrf-token', restamped);
          attempt -= 1;
          continue;
        }
      }

      // Only session/auth failures should clear the client session — RBAC 403s are route-specific.
      // STEP_UP_REQUIRED is a re-auth challenge, not a dead session.
      if (response.status === 401 || response.status === 404) {
        const isStepUpRequired = error.code === 'STEP_UP_REQUIRED';
        const isRoutingMismatch = error.message.includes('Multi-instance session routing mismatch');
        const isRefreshPath = normalizedPath.includes('/auth/refresh');

        // S1: Never sign out if it's just a Render routing mismatch. Let the user retry.
        if (isRoutingMismatch) {
          throw error;
        }

        if (response.status === 401) {
          if (isRefreshPath && !isNativeCapacitorClient()) {
            clearBrowserSessionMarkers();
          }
          if (!isRefreshPath && !isNativeCapacitorClient() && !isStepUpRequired && attempt === 0) {
            const refreshed = await tryRefreshBrowserSession();
            if (refreshed === 'ok') {
              // Refresh rotates the access JWT in storage — retry MUST use the new
              // Bearer. Reusing the pre-loop Authorization header causes a second
              // 401 and a false "session expired" logout (common while approving drafts).
              const nextAccess = getStoredAccessToken();
              if (nextAccess) {
                headers.set('Authorization', `Bearer ${nextAccess}`);
              } else {
                headers.delete('Authorization');
                const csrfToken = (await ensureReadableCsrfToken()) || readCookie('csrf_token');
                if (csrfToken) headers.set('x-csrf-token', csrfToken);
              }
              continue;
            }
            // Network/CSRF/5xx during refresh — keep session and surface original 401.
            if (refreshed === 'transient') {
              throw error;
            }
          }
          if (authFailureHandler && !isStepUpRequired) {
            authFailureHandler(error);
          }
        }
      }

      if (shouldRetry(response.status, normalizedMethod) && attempt < retries) {
        await sleep(computeRetryDelay(retryDelayMs, attempt));
        continue;
      }

      throw error;
    } catch (error) {
      const isAbort = error instanceof DOMException && error.name === 'AbortError';
      if (isAbort && signal?.aborted) {
        throw new ApiError('Request aborted by caller', 0, { message: 'Request aborted by caller' });
      }
      if (isAbort && attempt < retries && RETRYABLE_METHODS.has(normalizedMethod)) {
        await sleep(computeRetryDelay(retryDelayMs, attempt));
        continue;
      }
      if (error instanceof ApiError) {
        if (shouldRetry(error.status, normalizedMethod) && attempt < retries) {
          await sleep(computeRetryDelay(retryDelayMs, attempt));
          continue;
        }
        throw error;
      }
      if (attempt < retries) {
        await sleep(computeRetryDelay(retryDelayMs, attempt));
        continue;
      }
      throw toApiError(error, 'Network request failed', {
        path: normalizedPath,
        method: normalizedMethod
      });
    }
  }

  throw new ApiError('Retry policy exhausted', 0, { message: 'Retry policy exhausted' });
}

export async function apiBlobRequest(path: string, options: ApiRequestOptions = {}): Promise<Blob> {
  const {
    timeoutMs: inputTimeoutMs = DEFAULT_TIMEOUT_MS,
    query,
    headers: inputHeaders,
    signal,
    method = 'GET',
    ...restOptions
  } = options;

  const normalizedPath = String(path || '').trim();
  if (!normalizedPath) {
    throw new ApiError('API path is required', 0, { message: 'API path is required' });
  }

  const timeoutMs = normalizeDuration(inputTimeoutMs, DEFAULT_TIMEOUT_MS, MIN_TIMEOUT_MS);
  const url = buildRequestUrl(normalizedPath, query);
  const normalizedMethod = method.toUpperCase();
  const headers = withTraceHeaders(new Headers(inputHeaders || {}));
  if (isNativeCapacitorClient()) {
    headers.set('x-ninpo-client', 'capacitor');
  }
  const activeBearerToken = isNativeCapacitorClient() ? readNativeAccessToken() : getStoredAccessToken();
  if (activeBearerToken && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${activeBearerToken}`);
  }

  const { signal: requestSignal, cleanup: cleanupAbort } = mergeAbortSignals(signal ?? undefined, timeoutMs);
  let response: Response;
  try {
    response = await fetch(url, {
      ...restOptions,
      method: normalizedMethod,
      headers,
      signal: requestSignal,
      credentials: isNativeCapacitorClient() ? 'omit' : 'include'
    });
  } finally {
    cleanupAbort();
  }

  if (!response.ok) {
    throw new ApiError(`Failed to fetch file (${response.status})`, response.status, null);
  }

  return await response.blob();
}

